/**
 * lot 원가 유틸 — PurchaseDB.매입정보 를 "입고 lot 목록" 으로 읽는다.
 *
 * 정본: docs/coupass-logistics-chain.md §4 "원가는 lot 단위다"
 *
 * 같은 SKU라도 지나온 경로와 시점에 따라 원가가 다르다.
 * 그저께 100개 @A, 어제 100개 @B(환율이 달라졌다) — 대표단가 하나로 뭉개면
 * 재고자산도 판매원가도 틀어진다.
 *
 * CommonJS 로 작성 — server.js(Node `require`)와 code-vue(webpack `import`)가 공용한다.
 * 판매장부의 매입원가는 서버 요약(getLedgerMonthlySummary)과 프론트
 * (calculateProductMetricsV3)가 **같은 함수**를 불러야 한다. 식을 양쪽에 복붙하면
 * 한쪽만 고쳐졌을 때 요약장부와 상품별 화면의 원가가 조용히 갈린다.
 * (eslint env.node=true 라 프론트에서도 module.exports 허용)
 *
 * ⚠ 안전 원칙: 옛 항목({date, 매입가})에는 수량·입고ID 가 없다.
 *   그런 구간은 lot 계산을 하지 않고 지금까지 쓰던 대표값으로 돌아간다.
 *   새 필드가 없다고 화면이 비거나 0 이 되면 안 된다.
 */

function num(v) {
    const n = parseFloat(v);
    return isFinite(n) ? n : 0;
}

/**
 * 매입가가 실제로 적힌 행만 고른다.
 *
 * 입고내역이 깔아주는 뼈대 행(`source:'inbound'`)은 날짜·수량만 있고 매입가가 비어 있다.
 * 그 행이 배열 맨 뒤에 오면, "마지막 행 = 이 상품 대표 매입가" 로 읽던 코드들이
 * 매입가를 0 으로 읽어 마진·ROI·재고금액·요약장부 원가가 통째로 틀어진다.
 * ⇒ 대표값을 뽑을 때는 **가격이 적힌 행**만 본다.
 *
 * 기존 데이터는 전 행에 가격이 있어서 결과가 지금과 완전히 같다.
 */
function 가격행들(매입정보) {
    if (!Array.isArray(매입정보)) return [];
    return 매입정보.filter(r => r && num(r.매입가) > 0);
}

/**
 * 대표 매입가 — "배열의 마지막 행" 이 아니라 "**가격이 적힌** 마지막 행".
 *
 * 배열은 날짜 오름차순이고 마지막이 최신이라는 규칙은 그대로다(growthdb.vue changeCost SoT).
 * 없으면 0 — 부르는 쪽이 지금처럼 빈 값/기본값으로 떨어지게 둔다.
 */
function 대표매입가(매입정보) {
    const rows = 가격행들(매입정보);
    if (!rows.length) return 0;
    // ⚠ parseInt 로 자른다 — 옛 코드가 전부 `parseInt(...매입가)` 였다.
    //   실측(2026-08-31): 매입가가 소수인 상품이 322개 있다("766.7", 6213.93168).
    //   parseFloat 로 바꾸면 그 상품들의 대표 매입가·마진이 조용히 달라진다.
    const n = parseInt(rows[rows.length - 1].매입가, 10);
    return isFinite(n) ? n : 0;
}

/**
 * 그 판매일에 유효했던 매입가 — 판매일 이전(같은 날 포함) 중 가장 최신.
 *
 * 이전 기록이 없으면 대표매입가로 근사한다(기존 서버·프론트 폴백과 같은 동작).
 * 여기서도 **가격이 적힌 행만** 본다 — 가격 없는 뼈대 행이 판매일 직전에 끼면
 * 그 상품 원가가 조용히 0 이 되고 "매입가 없는 상품" 으로 잘못 집계된다.
 */
function 매입가ByDate(매입정보, date) {
    const rows = 가격행들(매입정보);
    let best = null;
    for (const r of rows) {
        if (!r.date || r.date > date) continue;
        if (best === null || r.date > best.date) best = r;
    }
    return best ? num(best.매입가) : 대표매입가(매입정보);
}

/** 수량이 붙은 lot 만 골라 날짜 오름차순으로. 없으면 빈 배열. */
function lotsOf(매입정보) {
    if (!Array.isArray(매입정보)) return [];
    return 매입정보
        .filter(r => r && num(r.수량) > 0 && num(r.매입가) > 0)
        .map(r => ({
            date: r.date || '',
            매입가: num(r.매입가),
            수량: num(r.수량),
            입고ID: r.입고ID || '',
            잠정: !!r.잠정
        }))
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

/**
 * 입고ID(=쿠팡 shipmentId) 로 lot 단가를 바로 찾는다.
 * 입고 하나가 곧 lot 이라 FIFO 재구성이 필요 없다 — 정확히 그 lot 의 원가다.
 * @returns {Object} { 입고ID: 매입가 }
 */
function lotPriceByInboundId(매입정보) {
    const map = {};
    lotsOf(매입정보).forEach(l => { if (l.입고ID) map[l.입고ID] = l.매입가; });
    return map;
}

/**
 * FIFO 계산용 lot 큐 — 수량이 붙은 줄을 날짜 오름차순으로 놓고 단가를 채운다.
 *
 * 수량은 있는데 매입가가 아직 없는 lot 은 **큐에서 빼지 않는다.**
 * 빼면 뒤 lot 이 그 자리를 차지해 순서가 밀린다. 자리는 지키고 단가만 직전 lot 것을 승계한다.
 *
 * fifoUnitCost 와 fifoUnitCostByDate 가 같이 쓴다 — 큐 준비가 갈리면 두 함수 값이 어긋난다.
 *
 * @returns {Array|null} [{ date, 매입가, 잔여 }] — lot 이 없거나 단가를 하나도 모르면 null
 */
function lot큐(매입정보) {
    // 수량이 붙은 lot 전부 — 매입가가 없는 것도 자리를 지킨다
    const q = (Array.isArray(매입정보) ? 매입정보 : [])
        .filter(r => r && num(r.수량) > 0)
        .map(r => ({ date: r.date || '', 매입가: num(r.매입가), 잔여: num(r.수량) }))
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    if (!q.length) return null;

    // 매입가 없는 lot(입고내역이 깔아준 뼈대) 은 **그 적치일에 유효했던 매입가**를 먼저 쓴다.
    //
    // ⚠ 이 한 줄이 없으면 뼈대가 아무 값도 못 만든다. 큐(q)에는 `수량` 이 있는 줄만 들어오는데,
    //   사람이 넣어둔 수기 매입가는 `{date, 매입가}` 라 수량이 없어서 큐 밖에 있다.
    //   그러면 큐가 전부 무가격이 되어 아래 `단가를 하나도 모르면 계산하지 않는다` 에 걸린다.
    //   (실측 2026-08-31: 이걸 빼먹으면 lot 원가를 타기 시작하는 상품이 11,021개 중 0개다)
    q.forEach(l => { if (!(l.매입가 > 0)) l.매입가 = 매입가ByDate(매입정보, l.date); });

    // 그래도 모르면 직전 lot 단가를 승계한다. 앞이 비어 있으면 뒤에서 당겨온다
    let carry = 0;
    q.forEach(l => { if (l.매입가 > 0) carry = l.매입가; else l.매입가 = carry; });
    carry = 0;
    for (let i = q.length - 1; i >= 0; i--) {
        if (q[i].매입가 > 0) carry = q[i].매입가;
        else q[i].매입가 = carry;
    }
    if (!q.some(l => l.매입가 > 0)) return null;   // 단가를 하나도 모르면 계산하지 않는다
    return q;
}

/** 판매내역을 날짜 오름차순으로 정리한다. 수량 조건은 부르는 쪽이 건다 */
function 판매정렬(판매내역, 양수만) {
    return 판매내역
        .filter(s => s && (양수만 ? num(s.수량) > 0 : true))
        .map(s => ({ date: s.date || '', 수량: num(s.수량) }))
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

/**
 * 입고 순서대로 깎아서 "이 기간 판매분" 의 원가 단가를 낸다.
 *
 * ★ 재고를 쓰지 않는다. 입고 목록(lot)과 실제 판매량만으로 계산한다.
 *
 *   예전에는 '지금 남은 재고' 에서 거꾸로 세어 판매분을 집었는데, 그러면
 *   (1) 재고를 언제 수집했느냐에 따라 원가가 흔들리고
 *   (2) 기간을 월별로 좁히면 '가장 최근에 팔린 몫' 을 그 달 것으로 집어 틀린 값이 나왔다.
 *       (1월 lot 1000@1000 · 8월 lot 1000@2000, 1월 판매 1000개 → 1500원이 나왔다. 1000원이어야 한다)
 *
 *   그래서 앵커를 '남은 재고'(흔들림)에서 '첫 입고'(과거라 고정)로 옮긴다.
 *   재고는 검산용이지 계산 입력이 아니다.
 *
 * 날짜가 아니라 **수량이 떨어져야** 다음 lot 으로 넘어간다.
 *   2/15 lot 이 들어왔어도 1/15 lot 이 남아 있으면 계속 1/15 단가다.
 *
 * 가격을 빌려 쓰는 두 경우 — 수량은 깎지 않는다:
 *   - 첫 lot 보다 이른 판매 (1/15 입고인데 1/1 판매) → 첫 lot 단가
 *   - lot 을 다 쓴 뒤의 판매 (아직 안 잡힌 입고분)   → 마지막 lot 단가
 *
 * ⚠ 이 함수는 기간 전체에서 단가를 **하나**만 낸다. 그걸 일별 수량에 곱하면 날짜마다
 *   기간 평균이 붙는다(2026-09-14 실측). 일별 화면은 fifoUnitCostByDate 를 쓴다.
 *   여긴 "이 기간 판매분의 평균 원가" 가 필요한 자리에만 남겨둔다.
 *
 * @param {Array} 매입정보
 * @param {Array} 판매내역  [{ date:'YYYY-MM-DD', 수량 }] — **전체 기간**을 준다.
 *                          2월만 보더라도 1월 판매를 알아야 lot 이 얼마나 깎였는지 안다
 * @param {Object} [기간]   { from, to } 원가를 집계할 구간. 없으면 판매내역 전체
 * @returns {{단가:number, 원가:number, 수량:number, 근거:string}|null}
 *          lot 이 없으면 null → 부르는 쪽이 대표값으로 돌아간다
 */
function fifoUnitCost(매입정보, 판매내역, 기간) {
    if (!Array.isArray(판매내역)) return null;

    const q = lot큐(매입정보);
    if (!q) return null;

    const from = 기간 && 기간.from;
    const to = 기간 && 기간.to;
    const inRange = d => (!from || d >= from) && (!to || d <= to);

    const sales = 판매정렬(판매내역, true);

    let i = 0;          // 큐 포인터
    let 원가 = 0;
    let 센수량 = 0;

    for (const s of sales) {
        let 남 = s.수량;
        const 집계 = inRange(s.date);
        while (남 > 0) {
            while (i < q.length && q[i].잔여 <= 0) i++;

            // lot 을 다 썼거나(뒤) 아직 안 들어왔다(앞) — 단가만 빌리고 수량은 깎지 않는다
            if (i >= q.length || q[i].date > s.date) {
                const 단가 = i >= q.length ? q[q.length - 1].매입가 : q[i].매입가;
                if (집계 && 단가 > 0) { 원가 += 남 * 단가; 센수량 += 남; }
                남 = 0;
                break;
            }

            const t = Math.min(q[i].잔여, 남);
            if (집계 && q[i].매입가 > 0) { 원가 += t * q[i].매입가; 센수량 += t; }
            q[i].잔여 -= t;
            남 -= t;
        }
    }

    if (센수량 <= 0) return null;
    return {
        단가: Math.round(원가 / 센수량),
        원가: Math.round(원가),
        수량: 센수량,
        근거: 'lot-fifo'
    };
}

/**
 * **날짜별** FIFO 단가 — `{ 'YYYY-MM-DD': 단가 }`.
 *
 * fifoUnitCost 는 기간 전체에서 단가를 **하나** 내는데, 부르는 쪽은 그걸 일별 수량에 곱한다.
 * 그래서 lot 이 중간에 바뀌면 **모든 날짜에 기간 평균이 붙는다.**
 *   실측(2026-09-14, onethirdlab 95726851964): 7/16 484 → 8/28 584 로 바뀐 상품인데
 *   연초~오늘로 조회하면 7월 판매도 9/13 판매도 똑같이 493(기간 평균)이 붙었다.
 *   9/13 매입원가 24,157원 = 493 × 49. 날짜별로 내면 584 × 49 = 28,616 이 맞다.
 *
 * 큐를 **한 번만** 걸으면서 날짜별로 원가/수량을 모은다. 날마다 다시 까는 게 아니라
 * 앞 날짜가 깎아놓은 상태를 그대로 이어받으므로 FIFO 순서가 유지된다.
 *
 * 기간 인자가 없다. 날짜별 단가는 **어느 기간으로 조회하든 같은 값**이라 자를 이유가 없다.
 * (기간 의존이 사라지는 게 이 함수의 핵심이다 — 적재본을 잘라 더해도 화면과 같아진다)
 *
 * 판매량이 0 이하인 날(반품이 더 많은 날)은 큐를 깎지 않는다. 대신 **직전 날짜 단가를
 * 물려준다** — 그 날도 원가를 곱해야 해서 값이 비면 안 된다. 앞이 없으면 큐 맨 앞 단가.
 *
 * @param {Array} 매입정보
 * @param {Array} 판매내역 [{ date, 수량 }] — **전체 기간**을 준다(fifoUnitCost 와 같다)
 * @returns {Object|null} 날짜 → 단가. lot 이 없으면 null → 부르는 쪽이 대표값으로 돌아간다
 */
function fifoUnitCostByDate(매입정보, 판매내역) {
    const m = fifoLotByDate(매입정보, 판매내역);
    if (!m) return null;
    const out = {};
    Object.keys(m).forEach(d => { out[d] = m[d].매입가; });
    return out;
}

/**
 * lot 에 그 입고의 밀크런 원/개를 붙인다 (#112).
 *
 * 밀크런정보 = GrowthDB.밀크런정보 [{ date: 도착일, milkrunSeq, 밀크런비용: 원/개(= 그 입고 배분액 ÷ 요청수량) }]
 * lot 날짜(입고 적치일)와 밀크런 도착일은 하루이틀 어긋나고 서로 잇는 키(입고ID↔milkrunSeq)가 없다.
 * 그래서 ±7일 안의 (lot, 밀크런) 짝을 **날짜 차이 작은 것부터 전체에서** 짝짓는다(1:1). 짝이 없으면 밀크런 없이 들어온 lot → 0.
 * 같은 차이면 앞 lot·앞 밀크런 행이 먼저. ⚠ lot 순서대로 먼저 집으면 앞 lot 이 뒤 lot 의 짝을 빼앗는다
 *   (#4 샤워타월 95727932757: 08-09 lot 이 08-13 밀크런을 가져가 08-13 lot 2000개가 0원).
 */
function 밀크런붙이기(q, 밀크런정보) {
    const pool = (Array.isArray(밀크런정보) ? 밀크런정보 : [])
        .filter(m => m && m.date)
        .map(m => ({ t: Date.parse(m.date), 원: num(m.밀크런비용), used: false }))
        .filter(m => isFinite(m.t));
    const 짝 = [];
    q.forEach((l, li) => {
        l.밀크런 = 0;
        const t = Date.parse(l.date);
        if (!isFinite(t)) return;
        pool.forEach((m, mi) => {
            const gap = Math.abs(m.t - t);
            if (gap <= 7 * 86400000) 짝.push({ l, li, m, mi, gap });
        });
    });
    짝.sort((a, b) => a.gap - b.gap || a.li - b.li || a.mi - b.mi);
    짝.forEach(p => {
        if (p.m.used || p.l.짝됨) return;
        p.m.used = true; p.l.짝됨 = true; p.l.밀크런 = p.m.원;
    });
    q.forEach(l => { delete l.짝됨; });
}

/**
 * lot 이 없는 상품의 밀크런 원/개 — 그 판매일에 들어와 있던 가장 최근 입고의 값(매입가ByDate 와 같은 규칙).
 * 판매일 앞에 입고가 없으면 가장 이른 입고 값(매입가ByDate 의 대표값 폴백과 같은 자리). 밀크런 기록이 없으면 0.
 */
function 밀크런ByDate(밀크런정보, date) {
    const rows = (Array.isArray(밀크런정보) ? 밀크런정보 : []).filter(m => m && m.date)
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    if (!rows.length) return 0;
    let best = null;
    for (const r of rows) { if (r.date <= date) best = r; }
    return num((best || rows[0]).밀크런비용);
}

/**
 * 상품관리 밀크런비용 칸 표시 (#4) — lot 근거값만 쓴다. 근거값이 0/없을 때 옛 저장값(밀크런비용)으로 덮지 않는다(사장님 ⓑ).
 *   근거값 > 0                         → { 상태: '값', 원 }
 *   근거값 0/없음 + 밀크런 입고 기록 있음 → { 상태: '정보없음' } — 지금 lot 에 짝지을 밀크런 기록이 없다(수집 누락이거나 밀크런 아닌 입고)
 *   밀크런 입고 기록 자체가 없음          → { 상태: '해당없음' } — «-»
 * @param {Object} [basis] /api/growth/getUnitCostBasis 의 그 옵션 값({ 밀크런 })
 * @param {Array} [밀크런정보] GrowthDB.밀크런정보
 */
function 밀크런표시(basis, 밀크런정보) {
    const 원 = Math.round(basis && basis.밀크런 != null ? num(basis.밀크런) : 0);
    if (원 > 0) return { 상태: '값', 원 };
    const 기록 = (Array.isArray(밀크런정보) ? 밀크런정보 : []).some(m => m && m.date);
    return { 상태: 기록 ? '정보없음' : '해당없음', 원: 0 };
}

/**
 * **날짜별** FIFO lot — `{ 'YYYY-MM-DD': { 매입가, 밀크런, 입고일 } }`. fifoUnitCostByDate 의 본체.
 *
 * 매입가와 밀크런은 **같은 lot** 을 따라간다(#112 사장님 원칙: 판매 1개마다 그 물건 입고 lot 의
 * 밀크런 총액 ÷ 입고 수량). 큐를 한 번 걸으면서 같은 수량 t 에 매입가·밀크런을 함께 곱해 모은다.
 * 매입가 = round(원가/수량) 로 예전 fifoUnitCostByDate 와 같은 값이고, 밀크런은 반올림하지 않는다
 * (판매량을 곱해 비용이 되므로 반올림 오차가 쌓이지 않게).
 * 입고일 = 그 날 판매가 빠져나간 lot 날짜(여러 개면 쉼표).
 *
 * @param {Array} 매입정보
 * @param {Array} 판매내역 [{ date, 수량 }] — 전체 기간
 * @param {Array} [밀크런정보] GrowthDB.밀크런정보. 없으면 밀크런 0
 * @returns {Object|null} lot 이 없으면 null
 */
function fifoLotByDate(매입정보, 판매내역, 밀크런정보) {
    if (!Array.isArray(판매내역)) return null;

    const q = lot큐(매입정보);
    if (!q) return null;
    밀크런붙이기(q, 밀크런정보);

    const sales = 판매정렬(판매내역, false);

    let i = 0;          // 큐 포인터
    const 누적 = {};     // date → { 원가, 밀크런, 수량, 입고일 }
    const 무판매 = [];   // 판매량 0 이하인 날 — 마지막에 직전 단가를 물려준다
    const out = {};

    for (const s of sales) {
        if (s.수량 <= 0) {
            // 큐를 건드리지 않는다. 자리만 잡아둔다
            무판매.push(s.date);
            continue;
        }

        let 남 = s.수량;
        let 원가 = 0;
        let 밀크런 = 0;
        let 센수량 = 0;
        const 입고일 = [];
        while (남 > 0) {
            while (i < q.length && q[i].잔여 <= 0) i++;

            // lot 을 다 썼거나(뒤) 아직 안 들어왔다(앞) — 단가만 빌리고 수량은 깎지 않는다
            if (i >= q.length || q[i].date > s.date) {
                const l = i >= q.length ? q[q.length - 1] : q[i];
                if (l.매입가 > 0) { 원가 += 남 * l.매입가; 밀크런 += 남 * l.밀크런; 센수량 += 남; 입고일.push(l.date); }
                남 = 0;
                break;
            }

            const t = Math.min(q[i].잔여, 남);
            if (q[i].매입가 > 0) { 원가 += t * q[i].매입가; 밀크런 += t * q[i].밀크런; 센수량 += t; 입고일.push(q[i].date); }
            q[i].잔여 -= t;
            남 -= t;
        }

        // 같은 날짜가 여러 줄로 와도(호출부가 안 접은 경우) 가중평균이 되게 쌓아둔다
        if (센수량 > 0) {
            const a = 누적[s.date] || (누적[s.date] = { 원가: 0, 밀크런: 0, 수량: 0, 입고일: [] });
            a.원가 += 원가;
            a.밀크런 += 밀크런;
            a.수량 += 센수량;
            입고일.forEach(d => { if (a.입고일.indexOf(d) < 0) a.입고일.push(d); });
        }
    }

    Object.keys(누적).forEach(d => {
        const a = 누적[d];
        out[d] = { 매입가: Math.round(a.원가 / a.수량), 밀크런: a.밀크런 / a.수량, 입고일: a.입고일.join(',') };
    });
    if (!Object.keys(out).length) return null;

    // 판매량 0 이하인 날 — 그 날 이전의 가장 최근 단가를 물려준다. 앞이 없으면 첫 단가
    const 있는날 = Object.keys(out).sort();
    무판매.forEach(d => {
        if (out[d] != null) return;
        let 물림 = null;
        for (const k of 있는날) {
            if (k > d) break;
            물림 = out[k];
        }
        out[d] = 물림 != null ? 물림 : out[있는날[0]];
    });

    return out;
}

/**
 * 지금 남아 있는 `수량` 개의 재고자산 금액.
 *
 * 남은 재고는 최근 lot 이다(정본 §4) — 오래된 것부터 팔려나가므로.
 * 그래서 최신 lot 부터 채워 넣으며 그 lot 의 단가로 값을 매긴다.
 *
 * 대표단가 하나를 곱하면 환율이 움직였을 때 재고자산이 틀어진다.
 *
 * @returns {{금액:number, 단가:number}|null} lot 이 없거나 모자라면 null → 대표값으로 돌아간다
 */
function lotStockValue(매입정보, 수량) {
    const q = Math.max(0, num(수량));
    if (q <= 0) return { 금액: 0, 단가: 0 };

    const lots = lotsOf(매입정보);
    if (!lots.length) return null;

    // lot 합이 남은 재고를 못 덮으면 값을 지어내게 된다. 그럴 땐 하지 않는다
    const 총lot = lots.reduce((a, l) => a + l.수량, 0);
    if (총lot < q) return null;

    let 남 = q;
    let 금액 = 0;
    for (let i = lots.length - 1; i >= 0 && 남 > 0; i--) {
        const t = Math.min(lots[i].수량, 남);
        금액 += t * lots[i].매입가;
        남 -= t;
    }
    return { 금액: Math.round(금액), 단가: Math.round(금액 / q) };
}

module.exports = { lotsOf, lotPriceByInboundId, fifoUnitCost, fifoUnitCostByDate, fifoLotByDate, 밀크런ByDate, 밀크런표시, lotStockValue, 가격행들, 대표매입가, 매입가ByDate };
